"use client";
import React, { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Button, Card, Flex, Heading, TextField, Text } from "@radix-ui/themes";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import { createClient } from "@/app/lib/supabase/client";
import { useRouter } from "next/navigation";
import { ErrorMsg } from "./FormField";
import { paths } from "@/app/lib/paths";

export const schema = z
  .object({
    email: z.string().email("Invalid email address."),
    password: z
      .string()
      .trim()
      .min(1, { message: "You must enter a password" }),
  })
  .required();

type Schema = z.infer<typeof schema>;

const client = createClient();

export function LoginForm() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const { errors } = form.formState;

  const onSubmit: SubmitHandler<Schema> = async (formData) => {
    setIsLoading(true);
    try {
      const { data, error } = await client.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (!data?.user || error) {
        setError("Invalid Email or Password.");
        return;
      }
      router.push(paths.root.getHref());
    } catch (error) {
      console.error("Sign in error", error);
      setError("Invalid Email or Password.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card size="2" className="max-w-[400px]">
      <Flex direction="column" gap="6">
        <Heading>Opperman & Son Admin Login</Heading>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Flex direction="column" gap="4">
            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                Email
              </Text>
              <TextField.Root
                {...form.register("email")}
                color={errors.email ? "crimson" : undefined}
                type="email"
                placeholder="Enter your email"
              />
              {errors.email && <ErrorMsg>{errors.email.message}</ErrorMsg>}
            </label>
            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                Passward
              </Text>
              <TextField.Root
                {...form.register("password")}
                color={errors.password ? "crimson" : undefined}
                type="password"
                placeholder="Enter your passward"
              />
              {errors.password && (
                <ErrorMsg>{errors.password.message}</ErrorMsg>
              )}
            </label>
          </Flex>
          <Flex flexGrow="1" mt="4" justify="center">
            <ErrorMsg>{error}</ErrorMsg>
          </Flex>
          <Flex gap="3" mt="4" flexGrow="1">
            <Button loading={isLoading} type="submit" className="w-full">
              <div className="w-full">Login</div>
            </Button>
          </Flex>
        </form>
      </Flex>
    </Card>
  );
}
